import { ControllerBatteryState, ControllerConnectionState } from "types/productTypes";

export const CONTROLLER_LOW_BATTERY_THRESHOLD = 20;
export const CONTROLLER_BATTERY_BLINK_INTERVAL_MS = 15000;
export const CONTROLLER_BATTERY_BLINK_ON_MS = 350;
export const CONTROLLER_BATTERY_BLINK_GAP_MS = 350;
export const CONTROLLER_BATTERY_BLINK_REPETITIONS = 3;

export type ControllerHand = "left" | "right";

export interface ControllerHandStatusSummary {
  hand: ControllerHand;
  connected: boolean;
  battery: number | null;
  lowBattery: boolean;
}

export function mapControllerConnectionState(
  value?: number | ControllerConnectionState | null,
): ControllerConnectionState | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string") {
    return value as ControllerConnectionState;
  }

  switch (value) {
    case -1:
      return ControllerConnectionState.Error;
    case 0:
      return ControllerConnectionState.None;
    case 1:
      return ControllerConnectionState.LeftOnly;
    case 2:
      return ControllerConnectionState.RightOnly;
    case 3:
      return ControllerConnectionState.Both;
    default:
      return null;
  }
}

export function normalizeControllerBatteryLevel(value: unknown): number | null {
  if (typeof value === "number" && !isNaN(value) && value >= 0 && value <= 100) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
      return parsed;
    }
  }

  return null;
}

export function normalizeControllerBattery(value: unknown): ControllerBatteryState | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  return {
    left: normalizeControllerBatteryLevel(candidate.left),
    right: normalizeControllerBatteryLevel(candidate.right),
  };
}

export function isControllerBatteryLow(value: number | null | undefined): boolean {
  return typeof value === "number" && value <= CONTROLLER_LOW_BATTERY_THRESHOLD;
}

export function isControllerHandConnected(
  controllerState: ControllerConnectionState | null | undefined,
  hand: ControllerHand,
): boolean {
  if (!controllerState || controllerState === ControllerConnectionState.Error) {
    return false;
  }

  if (hand === "left") {
    return (
      controllerState === ControllerConnectionState.LeftOnly ||
      controllerState === ControllerConnectionState.Both
    );
  }

  return (
    controllerState === ControllerConnectionState.RightOnly ||
    controllerState === ControllerConnectionState.Both
  );
}

export function getControllerBatterySummary(
  controllerState: ControllerConnectionState | null | undefined,
  controllerBattery: ControllerBatteryState | null | undefined,
  singleController = false,
): ControllerHandStatusSummary[] {
  const normalizedBattery = controllerBattery ?? { left: null, right: null };

  if (singleController) {
    const leftConnected = isControllerHandConnected(controllerState, "left");
    const rightConnected = isControllerHandConnected(controllerState, "right");
    const hand: ControllerHand = !leftConnected && rightConnected ? "right" : "left";
    const connected = leftConnected || rightConnected;
    const battery = !connected
      ? null
      : leftConnected !== rightConnected
        ? normalizedBattery[hand]
        : (normalizedBattery.left ?? normalizedBattery.right);
    return [
      {
        hand,
        connected,
        battery,
        lowBattery: isControllerBatteryLow(battery),
      },
    ];
  }

  return (["left", "right"] as ControllerHand[]).map((hand) => {
    const battery = isControllerHandConnected(controllerState, hand)
      ? normalizedBattery[hand]
      : null;
    return {
      hand,
      connected: isControllerHandConnected(controllerState, hand),
      battery,
      lowBattery: isControllerBatteryLow(battery),
    };
  });
}

export function hasLowControllerBattery(
  controllerBattery: ControllerBatteryState | null | undefined,
): boolean {
  if (!controllerBattery) {
    return false;
  }

  return (
    isControllerBatteryLow(controllerBattery.left) ||
    isControllerBatteryLow(controllerBattery.right)
  );
}

export const controllerBatteryTooltipStyles = {
  tooltip: {
    height: "auto" as const,
    minHeight: 132,
    padding: "14px 16px",
    lineHeight: 1.4,
    overflow: "visible" as const,
    backgroundColor: "#ffffff",
    color: "#1f2937",
    border: "1px solid rgba(148, 163, 184, 0.35)",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.16)",
  },
  arrow: {
    backgroundColor: "#ffffff",
    border: "1px solid rgba(148, 163, 184, 0.35)",
  },
};

export function isControllerBatteryBlinkActive(
  elapsedMs: number,
  blinkIntervalMs = CONTROLLER_BATTERY_BLINK_INTERVAL_MS,
): boolean {
  if (elapsedMs < 0) {
    return false;
  }

  const cycleElapsed = elapsedMs % blinkIntervalMs;

  for (let flashIndex = 0; flashIndex < CONTROLLER_BATTERY_BLINK_REPETITIONS; flashIndex += 1) {
    const flashStart =
      flashIndex * (CONTROLLER_BATTERY_BLINK_ON_MS + CONTROLLER_BATTERY_BLINK_GAP_MS);
    const flashEnd = flashStart + CONTROLLER_BATTERY_BLINK_ON_MS;

    if (cycleElapsed >= flashStart && cycleElapsed < flashEnd) {
      return true;
    }
  }

  return false;
}
